<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="computedTitle" @hidden="onHidden"
                   @show="onShow" @ok.prevent="validateAndSubmit(handleSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading" :content-in-form="false" scrollable>

      <div class="row">
        <div class="col-md-8">
          <b-form ref="mainForm" @submit.prevent="validateAndSubmit(handleSubmit)">
            <button type="submit" hidden></button>
            <b-alert variant="danger" :show="error && error.length > 0">
              {{ error }}
            </b-alert>
            <div class="row">
              <div class="col-lg-4 col-md-12">
                <customer-select-input-widget v-if="model.enable_customer_edit" :model="model" attribute="customer_uuid"
                                              expand="shipping_address" @select="onCustomerSelect"/>
                <input-widget v-else disabled :model="model" :label="$t('Customer')"
                              :placeholder="model.customer ? model.customer.display_name : ''"/>
              </div>
              <div class="col-lg-4 col-md-12" v-if="!isSalesTaxEnabled">
                <input-widget :model="model" attribute="tax_status_uuid" type="select2"
                              display-field="${item.name}" :items="taxStatuses" hint/>
              </div>
              <div class="col-lg-4 col-md-12">
                <input-widget :model="model" type="datepicker" attribute="receive_date"/>
              </div>
              <div class="col-md-12 col-lg-4">
                <item-location-select-input-widget :model="model" attribute="item_location_uuid"/>
              </div>
              <div class="col-md-12 col-lg-4">
                <department-select-input-widget @select="onDepartmentSelect" :model="model" @unselect="onDepartmentUnselect"
                                                attribute="department_uuid"/>
              </div>
              <div class="col-md-12 col-lg-4">
                <category-select-input-widget @select="onCategorySelect" get-dynamic-fields-template
                                              :model="model" :department="model.department" attribute="category_uuid"/>
              </div>
              <div class="col-md-12">
                <user-select-input-widget :model="model" attribute="specialists_uuids" multiple :items="model.specialists" :disabled="!model.department_uuid"
                                          :filter-extra-data="specialistExtraFilters"/>
              </div>
              <div class="col-md-6 col-lg-3">
                <input-widget :model="model" attribute="quantity" type="number"/>
              </div>
              <div class="col-md-6 col-lg-3">
                <temporary-item-progress-select-input-widget :model="model" attribute="progress" :options="{placeholder: null}"/>
              </div>
              <div class="col-md-6 col-lg-3">
                <temporary-item-potential-select-input-widget :model="model" attribute="potential" :options="{placeholder: null}"/>
              </div>
            </div>
            <translatable-input-widget :hint="false" :model="model" :fields="model.sorted_dynamic_fields_template"
                                       :errors="errors" v-if="!dynamicFieldsLoading"
                                       @onNewValuedAddedToDropdown="onNewValuedAddedToDropdown"/>
            <div class="row">
              <div class="col-md-12 col-lg-6">
                <user-select-input-widget :model="model" attribute="rep_user_uuid"/>
              </div>
              <div class="col-md-12 col-lg-6">
                <user-select-input-widget :model="model" attribute="expert_user_uuid"/>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_enabled" type="checkbox"
                              :is-switch="true"
                              size="lg" @change="setCollectionInformation"/>
              </div>
            </div>
            <div class="row" v-if="model.collection_info_enabled">
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_address_1"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_address_2"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_state"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_city"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_zip_code"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_phone"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_email"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_notes"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_first_name"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_last_name"/>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 col-lg-3">
                <currency-input-widget :model="model" attribute="low"/>
              </div>
              <div class="col-md-6 col-lg-3">
                <currency-input-widget :model="model" attribute="high"/>
              </div>
              <div class="col-md-6 col-lg-3">
                <currency-input-widget :model="model" attribute="start_price"/>
              </div>
              <div class="col-md-6 col-lg-3">
                <currency-input-widget :model="model" attribute="reserve"/>
              </div>
              <div class="col-md-6 col-lg-3">
                <reserve-type-select-input-widget :model="model" attribute="reserve_type"/>
              </div>
            </div>
            <div class="row">
              <div class="col-md-4">
                <input-widget :model="model" attribute="net_reserve" type="checkbox"/>
              </div>
            </div>
          </b-form>
        </div>
        <div class="col-md-4">
          <temporary-images-view ref="temporaryImagesView" :images="model && model.images ? model.images : []"
                                 @onImageRemove="onImageRemove"/>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import InputWidget from "../../../core/components/input-widget/InputWidget";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import UserSelectInputWidget from "../../../core/widgets/UserSelectInputWidget";
  import DepartmentSelectInputWidget from "../../../core/widgets/DepartmentSelectInputWidget";
  import CurrencyInputWidget from "../../../core/widgets/CurrencyInputWidget";
  import TranslatableInputWidget from "../../../core/widgets/TranslatableInputWidget";
  import ItemLocationSelectInputWidget from "../../../core/widgets/ItemLocationSelectInputWidget";
  import CategorySelectInputWidget from "../../../core/widgets/CategorySelectInputWidget";
  import TemporaryItemModel from "@/modules/items/temporary/TemporaryItem.model";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import TemporaryImagesView from "@/modules/items/temporary/TemporaryImagesView";
  import TemporaryItemsService from "@/modules/items/temporary/TemporaryItemsService";
  import i18n from "../../../shared/i18n";
  import TemporaryItemProgressSelectInputWidget from "../../../core/widgets/TemporaryItemProgressSelectInputWidget.vue";
  import TemporaryItemPotentialSelectInputWidget
    from "../../../core/widgets/TemporaryItemPotentialSelectInputWidget.vue";
  import ReserveTypeSelectInputWidget from "@/core/widgets/ReserveTypeSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('temporaryItems');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapActions: departmentMapActions} = createNamespacedHelpers('setup/department');

  export default {
    name: "TemporaryItemModal",
    components: {
      ReserveTypeSelectInputWidget,
      TemporaryItemPotentialSelectInputWidget,
      TemporaryItemProgressSelectInputWidget,
      TemporaryImagesView,
      CustomerSelectInputWidget,
      CategorySelectInputWidget,
      ItemLocationSelectInputWidget,
      TranslatableInputWidget,
      CurrencyInputWidget,
      DepartmentSelectInputWidget,
      UserSelectInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new TemporaryItemModel(),
        saleCurrency: null,
        deletedImageIndex: null,
        deleteAllImages: false,
        dynamicFieldsLoading: false,
        shouldRefreshAllDepartmentsAfterSave: false
      }
    },
    computed: {
      ...mapStateGlobal(['taxStatuses', 'isDynamicFieldsTemplateFromCategory', 'currentUser', 'isSalesTaxEnabled']),
      ...mapState({
        modal: state => state.modal,
        editObject: state => state.modal.object,
        error: state => state.modal.error,
        tableData: state => state.tableData[state.tableSource],
      }),
      computedTitle() {
        if (this.model && this.model.uuid) {
          return this.$i18n.t('Update Temporary Inventory Item');
        } else {
          return this.$i18n.t('New Temporary Inventory Item');
        }
      },
      specialistExtraFilters() {
        return {specialist_department_uuid: this.model && this.model.department_uuid ? this.model.department_uuid : null};
      }
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new TemporaryItemModel(this.editObject);
          if (this.model.images.length > 0) {
            this.$nextTick(() => this.$refs.temporaryImagesView.setInitialItemIndex())
          }
        }
      },
    },
    methods: {
      ...departmentMapActions(['getDepartments']),
      ...mapActions(['hideModal', 'updateTemporaryItem', 'createTemporaryItem', 'updateTemporaryItemLocally']),
      onNewValuedAddedToDropdown() {
        this.shouldRefreshAllDepartmentsAfterSave = true
      },
      onShow() {
        this.showConfirmation = true;
        this.model.rep_user = this.currentUser;
        this.model.rep_user_uuid = this.currentUser.uuid;

        if (!this.editObject) {
          this.model.enable_customer_edit = true;
        }
        this.dynamicFieldsLoading = true;

        this.$nextTick(() => {
          this.dynamicFieldsLoading = false;
        })

        // if (this.editObject && this.editObject.uuid) {
        //   this.model.collection_info_enabled = true;
        //   this.setCollectionInformation(true);
        // }
      },
      onHidden() {
        this.model = new TemporaryItemModel();
        this.hideModal();
      },
      onDepartmentSelect(department) {
        // this.dynamicFieldsLoading = true;
        this.model.department = department;
        this.model.category_uuid = null;
        this.model.specialists_uuids = [];
        this.model.specialists = [];
        // this.model.initDynamicFields(this.model, this.model.department.dynamic_fields_template)
        // this.model = new TemporaryItemModel(this.model, false);
        // this.$nextTick(() => {
        //   this.dynamicFieldsLoading = false;
        // })
      },
      onDepartmentUnselect() {
        this.model.specialists_uuids = [];
        this.model.specialists = [];
      },
      onCustomerSelect(customer) {
        this.model.collection_info_enabled = true;
        this.model.customer = customer;
        this.model.customer_uuid = customer.uuid;
        this.setCollectionInformation(true, true);
      },
      onCategorySelect(category) {
        this.dynamicFieldsLoading = true;
        this.model.category = category;
        this.model.initDynamicFields(this.model, this.model.category.dynamic_fields_template)
        this.model = new TemporaryItemModel(this.model, false);
        this.$nextTick(() => {
          this.dynamicFieldsLoading = false;
        })
      },
      async onSubmit() {
        this.loading = true;
        this.model.assignToDynamicFields();

        let deletedImageUuids = []
        if (this.deleteAllImages) {
          this.model.images.forEach(img => deletedImageUuids.push(img.uuid))
          this.model.images.splice(0, this.model.images.length);
        }

        /**
         * TODO when you delete image at index - 0, and move this image to other index,
         * `deletedImageIndex` does not change and saves old index
         *
         */
        if (this.deletedImageIndex || this.deletedImageIndex === 0) {
          deletedImageUuids.push((this.model.images[this.deletedImageIndex] || {}).uuid)
          this.model.images.splice(this.deletedImageIndex, 1);
        }
        deletedImageUuids = deletedImageUuids.filter(_ => _)

        let response, modelUuid;
        if (this.model && this.model.uuid) {
          response = await this.updateTemporaryItem({...this.model, images: []});
          modelUuid = this.model.uuid
        } else {
          let send_email_notifications = await this.$confirm(i18n.t('Would you like to send the notification to the selected customer and the department specialists?'));
          response = await this.createTemporaryItem({...this.model, images: [], skip_email_notifications: !send_email_notifications})
          modelUuid = response.body.uuid
        }

        let success = response.success
        let body = response.body
        let errorsList

        if (!success) {
          this.loading = false;

          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            if (body.non_field_errors) {
              this.$errorDialog(body.non_field_errors[0]);
            } else {
              this.model.setMultipleErrors(body);
            }
          }

          return;
        }

        try {
          const images = this.model.images
          await TemporaryItemsService.createRenameAndDeleteImages(modelUuid, images, '/api/v1/inventories/temporary-images/', () => {
          })
        } catch (err) {
          // handle error
          success = false;
          body = err.body;
          errorsList = err.errorsList;
        }

        if (this.shouldRefreshAllDepartmentsAfterSave) {
          this.getDepartments()
        }

        if (success) {
          const res = await TemporaryItemsService.getImages(modelUuid)
          // we need to do this as serverless takes some time to delete images, so records are actually deleted from database a bit later
          const images = res.body.filter(img => !deletedImageUuids.includes(img.uuid));
          if (this.model && this.model.uuid) {
            console.log({...this.model});
            // noinspection JSCheckFunctionSignatures
            this.updateTemporaryItemLocally({...this.model, images, specialists: body.specialists})
          } else {
            const thisModel = this.tableData.rows.find(item => item.uuid === modelUuid)
            // noinspection JSCheckFunctionSignatures
            this.updateTemporaryItemLocally({...thisModel, images, specialists: body.specialists})
          }

          this.loading = false;
          this.showConfirmation = false;
          // noinspection ES6MissingAwait
          this.hideModal();

          if (!this.model.uuid) {
            this.$toast('success', this.$t('Temporary item was successfully created'));
          } else {
            this.$toast('success', this.$t('Temporary Inventory item was successfully updated'));
          }
        } else {
          this.loading = false;
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else if (errorsList instanceof Array && errorsList.length > 0) {
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, errorsList))
          } else {
            // noinspection JSUnresolvedVariable
            if (body.non_field_errors) {
              this.$errorDialog(body.non_field_errors[0]);
            } else {
              this.model.setMultipleErrors(body);
            }
          }
        }

        this.deletedImageIndex = null;
        this.deleteAllImages = false;
      },
      setCollectionInformation(collection_enabled, customerChange = false) {
        if (!collection_enabled) {
          this.model.collection_info_address_1 = null;
          this.model.collection_info_address_2 = null;
          this.model.collection_info_state = null;
          this.model.collection_info_city = null;
          this.model.collection_info_zip_code = null;
          this.model.collection_info_phone = null;
          this.model.collection_info_first_name = null;
          this.model.collection_info_last_name = null;
          this.model.collection_info_email = null;
          this.model.collection_info_notes = null;
        } else {
          if (this.model.customer && this.model.customer.shipping_address) {
            if (customerChange) {
              this.model.collection_info_address_1 = this.model.customer.shipping_address.address_1;
              this.model.collection_info_address_2 = this.model.customer.shipping_address.address_2;
              this.model.collection_info_state = this.model.customer.shipping_address.state;
              this.model.collection_info_city = this.model.customer.shipping_address.city;
              this.model.collection_info_zip_code = this.model.customer.shipping_address.zip_code;
              this.model.collection_info_first_name = this.model.customer.shipping_address.first_name;
              this.model.collection_info_last_name = this.model.customer.shipping_address.last_name;
              this.model.collection_info_phone = this.model.customer.shipping_address.phone ? this.model.customer.shipping_address.phone.split(',')[0] : this.model.customer.phone ? this.model.customer.phone.split(',')[0] : null;
              this.model.collection_info_email = this.model.customer.shipping_address.email ? this.model.customer.shipping_address.email : this.model.customer.email ? this.model.customer.email : null;
            } else {
              this.model.collection_info_address_1 = this.model.collection_info_address_1 ? this.model.collection_info_address_1 : this.model.customer.shipping_address.address_1;
              this.model.collection_info_address_2 = this.model.collection_info_address_2 ? this.model.collection_info_address_2 : this.model.customer.shipping_address.address_2;
              this.model.collection_info_state = this.model.collection_info_state ? this.model.collection_info_state : this.model.customer.shipping_address.state;
              this.model.collection_info_city = this.model.collection_info_city ? this.model.collection_info_city : this.model.customer.shipping_address.city;
              this.model.collection_info_zip_code = this.model.collection_info_zip_code ? this.model.collection_info_zip_code : this.model.customer.shipping_address.zip_code;
              this.model.collection_info_first_name = this.model.collection_info_first_name ? this.model.collection_info_first_name : this.model.customer.shipping_address.first_name;
              this.model.collection_info_last_name = this.model.collection_info_last_name ? this.model.collection_info_last_name : this.model.customer.shipping_address.last_name;
              this.model.collection_info_phone = this.model.collection_info_phone ? this.model.collection_info_phone : this.model.customer.shipping_address.phone ? this.model.customer.shipping_address.phone.split(',')[0] : this.model.customer.phone ? this.model.customer.phone.split(',')[0] : null;
              this.model.collection_info_email = this.model.collection_info_email ? this.model.collection_info_email : this.model.customer.shipping_address.email ? this.model.customer.shipping_address.email : this.model.customer.email ? this.model.customer.email : null;
            }
          }
        }
      },
      onImageRemove(index, deleteAll) {
        this.deletedImageIndex = index;
        this.deleteAllImages = deleteAll;
      },
      async validateAndSubmit(handleSubmit) {
        await handleSubmit(this.onSubmit)

        const invalidInput = this.$refs.mainForm.querySelector('.has-error');
        if (invalidInput) {
          invalidInput.scrollIntoView({behavior: 'smooth'});
        }
      }
    },
  }
</script>

<style scoped>

</style>
